import { FC } from 'react';

import { CreditCard, Sparkles } from 'lucide-react';

import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import { Card, CardContent } from '../../../../components/ui/Card';
import { PremiumPackage } from '../../../../types';
import { cn } from '../../../../utils';

interface Props {
    premiumPackage: PremiumPackage;
    highestUnitPrice: number;
    recommended: boolean;
    loadingServiceId: number | null;
    onCheckout: (serviceId: number) => void;
}

const formatPrice = (price: number, currency: string) =>
    new Intl.NumberFormat('uk-UA', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(price / 100);

const activationLabel = (count: number) => {
    const mod10 = count % 10;
    const mod100 = count % 100;

    if (mod10 === 1 && mod100 !== 11) {
        return 'активація';
    }

    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
        return 'активації';
    }

    return 'активацій';
};

export const PremiumPackageCard: FC<Props> = ({
    premiumPackage,
    highestUnitPrice,
    recommended,
    loadingServiceId,
    onCheckout,
}) => {
    const unitPrice = premiumPackage.price / premiumPackage.premium_ads_count;
    const discountPercent =
        highestUnitPrice > 0
            ? Math.round((1 - unitPrice / highestUnitPrice) * 100)
            : 0;
    const isLoading = loadingServiceId === premiumPackage.id;

    return (
        <Card
            className={cn(
                'relative flex h-full flex-col overflow-hidden transition-shadow hover:shadow-md',
                recommended && 'border-primary/50 shadow-md',
            )}
            data-testid={`premium-package-card-${premiumPackage.code}`}
        >
            {recommended && (
                <div className="absolute top-0 right-0 left-0 flex items-center justify-center gap-1 bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                    <Sparkles className="h-3 w-3" />
                    Найвигідніший
                </div>
            )}

            <CardContent
                className={cn(
                    'flex h-full flex-col p-5',
                    recommended && 'pt-10',
                )}
            >
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h3 className="text-xl font-bold">
                            {premiumPackage.premium_ads_count}{' '}
                            {activationLabel(premiumPackage.premium_ads_count)}
                        </h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Кожна діє{' '}
                            {premiumPackage.premium_slot_duration_days} днів
                        </p>
                    </div>
                    {recommended && discountPercent > 0 && (
                        <Badge variant="success">−{discountPercent}%</Badge>
                    )}
                </div>

                <div className="mt-6">
                    <div className="text-3xl font-bold tracking-tight">
                        {formatPrice(
                            premiumPackage.price,
                            premiumPackage.currency,
                        )}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {formatPrice(unitPrice, premiumPackage.currency)} за
                        активацію
                    </p>
                </div>

                <Button
                    type="button"
                    className="mt-5 w-full"
                    variant={recommended ? 'default' : 'outlinePrimary'}
                    onClick={() => onCheckout(premiumPackage.id)}
                    disabled={loadingServiceId !== null}
                    aria-busy={isLoading}
                >
                    <CreditCard className="mr-2 h-4 w-4" />
                    {isLoading ? 'Створюємо оплату...' : 'Онлайн оплата'}
                </Button>
            </CardContent>
        </Card>
    );
};
